import ReactPhoneInput, { PhoneInputProps } from 'react-phone-input-2'
import { Box } from './Box'
import { Text } from './Text'
import { Span } from 'theme/globalStyleCss'
import { forwardRef, ReactNode, useRef } from 'react'
import { FieldError } from 'react-hook-form'
import { InputBoxStyle } from './InputField'

interface PhoneNumberFieldProps extends Omit<PhoneInputProps, 'onChange'> {
  label?: string
  required?: boolean
  name: string
  onChange: (value: string) => void
  error?: FieldError | string
  placeholder?: string
}

export const PhoneNumberField = forwardRef<
  HTMLInputElement,
  PhoneNumberFieldProps
>(({ label, required, name, error, onChange, ...props }, ref) => {
  const inputFieldRef = useRef<HTMLInputElement>(null)

  return (
    <>
      {label && (
        <Text
          as="label"
          htmlFor={name}
          css={{
            textTransform: 'capitalize',
            mb: '$space$8',
            display: 'block',
          }}
        >
          {label}
          {required ? (
            <Span css={{ color: '$error', ml: '$space$6' }}>*</Span>
          ) : null}
        </Text>
      )}
      <Box css={PhoneFieldCss} ref={inputFieldRef}>
        <ReactPhoneInput
          country={'gb'}
          containerStyle={ContainerCss}
          inputStyle={InputCss}
          dropdownStyle={{
            ...DropdownCss,
            width: `${inputFieldRef?.current?.clientWidth}px`,
          }}
          disableSearchIcon={true}
          inputProps={{
            name,
            required,
            autoFocus: false,
            ref,
          }}
          enableAreaCodes={true}
          onChange={value => {
            onChange(`+${value}`)
          }}
          countryCodeEditable={false}
          enableSearch
          searchPlaceholder={'Search Country Code'}
          {...props}
        />
      </Box>
      {!!error && (
        <Text color="error" className="errorField">
          {error as ReactNode}
        </Text>
      )}
    </>
  )
})

const PhoneFieldCss = {
  '.react-tel-input': {
    'fontFamily': 'Inter',
    'background': '$inputBg',
    'border': '1px solid $inputBorder',
    'borderRadius': '$radii$sm',
    'py': 0.5,
    '.form-control': {
      color: '$blackBase',
      background: '$inputBg',
      border: 'none',
      borderRadius: '$radii$sm',
    },
    '.flag-dropdown': {
      'border': 'none',
      'background': '$inputBg',
      'borderRadius': '$radii$sm',
      '.country-list': {
        '&::-webkit-scrollbar': {
          width: 8,
          height: 12,
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          borderRadius: 0,
          my: '$space$8',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '$border',
          width: 8,
          borderRadius: 8,
        },
      },
      '&.open': {
        'background': '$inputBg',
        'borderRadius': '$radii$sm',
        '.selected-flag': { background: '$inputBg', borderRadius: '$radii$sm' },
      },
      '.selected-flag:focus': {
        background: '$inputBg',
        borderRadius: '$radii$sm',
      },
      '.selected-flag:hover': {
        background: '$inputBg',
        borderRadius: '$radii$sm',
      },
    },
    '.country-list .search-box': {
      ...InputBoxStyle,
      width: '100%',
      margin: 0,
      height: 38,
      color: '$blackBase',
    },
  },
  '.react-tel-input:hover': {
    borderColor: '$inputBorderFocus',
    boxShadow: '$inputShadow',
  },
}
const ContainerCss = {
  border: '1px solid $gray200',
}

const InputCss = {
  width: '100%',
  color: '$blackBase',
  height: 38,
}

const DropdownCss = {
  border: '1px solid $inputBorder',
  borderRadius: '$radii$sm',
}
